// ---------------------------------------------------------------- JSON Patch
const escape = (key) => key.replace(/~/g, '~0').replace(/\//g, '~1');
const unescape = (key) => key.replace(/~1/g, '/').replace(/~0/g, '~');
const tokens = (pointer) => pointer.split('/').slice(1).map(unescape);
const clone = (value) => structuredClone(value);
function parentOf(root, pointer) {
    const parts = tokens(pointer);
    if (parts.length === 0)
        throw new Error('FORM_PATCH_ROOT');
    let node = root;
    for (const part of parts.slice(0, -1)) {
        if (Array.isArray(node))
            node = node[Number(part)];
        else if (node !== null && typeof node === 'object')
            node = node[part];
        else
            node = undefined;
        if (node === undefined || node === null || typeof node !== 'object')
            throw new Error(`FORM_PATCH_PATH:${pointer}`);
    }
    return { parent: node, key: parts[parts.length - 1] };
}
function index(array, key, forAdd) {
    if (forAdd && key === '-')
        return array.length;
    if (!/^(0|[1-9][0-9]*)$/.test(key))
        throw new Error(`FORM_PATCH_INDEX:${key}`);
    const at = Number(key);
    if (at > array.length || (!forAdd && at >= array.length))
        throw new Error(`FORM_PATCH_INDEX:${key}`);
    return at;
}
function read(root, pointer) {
    const { parent, key } = parentOf(root, pointer);
    const value = Array.isArray(parent) ? parent[index(parent, key, false)] : parent[key];
    if (value === undefined)
        throw new Error(`FORM_PATCH_PATH:${pointer}`);
    return value;
}
function removeAt(root, pointer) {
    const { parent, key } = parentOf(root, pointer);
    if (Array.isArray(parent))
        return parent.splice(index(parent, key, false), 1)[0];
    if (!(key in parent))
        throw new Error(`FORM_PATCH_PATH:${pointer}`);
    const value = parent[key];
    delete parent[key];
    return value;
}
function addAt(root, pointer, value) {
    const { parent, key } = parentOf(root, pointer);
    if (Array.isArray(parent))
        parent.splice(index(parent, key, true), 0, value);
    else
        parent[key] = value;
}
/** Applique un patch sur une copie du corps ; une opération impossible lève une erreur, rien n'est appliqué à moitié. */
export function applyFormPatch(body, patch) {
    const copy = clone(body);
    for (const op of patch) {
        if (op.op === 'add')
            addAt(copy, op.path, clone(op.value));
        else if (op.op === 'remove')
            removeAt(copy, op.path);
        else if (op.op === 'replace') {
            read(copy, op.path);
            const { parent, key } = parentOf(copy, op.path);
            if (Array.isArray(parent))
                parent[index(parent, key, false)] = clone(op.value);
            else
                parent[key] = clone(op.value);
        }
        else if (op.op === 'move') {
            if (op.from === undefined)
                throw new Error('FORM_PATCH_FROM');
            addAt(copy, op.path, removeAt(copy, op.from));
        }
        else
            throw new Error(`FORM_PATCH_OP:${String(op.op)}`);
    }
    return copy;
}
const text = (label, locale, fallback) => {
    if (label === null || typeof label !== 'object')
        return fallback;
    const values = label;
    return values[locale] ?? values['fr'] ?? Object.values(values)[0] ?? fallback;
};
function pagesOf(body) {
    return body['layout']?.['pages'];
}
function blocksOf(body) {
    return body['layout']?.['blocks'];
}
/** Retrouve un élément par ses identifiants ; `null` s'il n'existe pas. */
export function locateFormElement(body, ref, locale = 'fr') {
    const sections = body.sections;
    if (ref.element_kind === 'section' || ref.element_kind === 'field') {
        const sectionIndex = sections.findIndex((section) => section['section_id'] === ref.section_id);
        if (sectionIndex < 0)
            return null;
        const section = sections[sectionIndex];
        if (ref.element_kind === 'section')
            return { pointer: `/sections/${sectionIndex}`, label: text(section['label'], locale, String(ref.section_id)), index: sectionIndex };
        const fields = section['fields'];
        const fieldIndex = fields.findIndex((entry) => entry['field']['field_id'] === ref.field_id);
        if (fieldIndex < 0)
            return null;
        return { pointer: `/sections/${sectionIndex}/fields/${fieldIndex}`, label: text(fields[fieldIndex]['field']['label'], locale, String(ref.field_id)), section_index: sectionIndex, index: fieldIndex };
    }
    if (ref.element_kind === 'page') {
        const pages = pagesOf(body) ?? [];
        const pageIndex = pages.findIndex((page) => page['page_id'] === ref.page_id);
        return pageIndex < 0 ? null : { pointer: `/layout/pages/${pageIndex}`, label: text(pages[pageIndex]['label'], locale, String(ref.page_id)), index: pageIndex };
    }
    const blocks = blocksOf(body) ?? [];
    const blockIndex = blocks.findIndex((block) => block['block_id'] === ref.block_id);
    return blockIndex < 0 ? null : { pointer: `/layout/blocks/${blockIndex}`, label: text(blocks[blockIndex]['label'], locale, String(ref.block_id)), index: blockIndex };
}
/**
 * Complète la partie d'un champ de repère quand le modèle ne l'a pas donnée : la partie du conteneur, ou la
 * seule partie qui porte ce champ. Plusieurs parties ou aucune : la référence reste telle quelle (refus plus loin).
 */
function withSiblingSection(body, container, sibling) {
    if (sibling?.element_kind !== 'field' || sibling.section_id !== undefined)
        return sibling;
    if (container.element_kind === 'section')
        return container.section_id === undefined ? sibling : { ...sibling, section_id: container.section_id };
    const holders = body.sections.filter((section) => section['fields'].some((entry) => entry['field']['field_id'] === sibling.field_id));
    return holders.length === 1 ? { ...sibling, section_id: String(holders[0]['section_id']) } : sibling;
}
/** Identifiants de champ déjà pris, toutes parties confondues. */
function fieldIds(body) {
    return new Set(body.sections.flatMap((section) => section['fields'].map((entry) => String(entry['field']['field_id']))));
}
/** Rend un identifiant unique par un suffixe (`poids`, `poids_2`…). */
function unique(base, taken) {
    if (!taken.has(base))
        return base;
    for (let n = 2;; n += 1)
        if (!taken.has(`${base}_${n}`))
            return `${base}_${n}`;
}
/** Slug d'un libellé : minuscules sans accents, `_` entre les mots. */
export function formElementSlug(label) {
    const slug = label
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 60);
    return /^[a-z]/.test(slug) ? slug : `champ_${slug}`.replace(/_+$/, '');
}
// ---------------------------------------------------------------- Phrases
const PHRASES = {
    fr: {
        add: (what, where) => `Ajouter « ${what} »${where}`,
        move: (what, where) => `Déplacer « ${what} »${where}`,
        update: (what, how) => `Modifier « ${what} » : ${how}`,
        remove: (what) => `Supprimer « ${what} »`,
        after: (other) => ` sous « ${other} »`,
        before: (other) => ` au-dessus de « ${other} »`,
        into: (other) => ` dans « ${other} »`,
        required: () => ', obligatoire',
    },
    en: {
        add: (what, where) => `Add “${what}”${where}`,
        move: (what, where) => `Move “${what}”${where}`,
        update: (what, how) => `Change “${what}”: ${how}`,
        remove: (what) => `Remove “${what}”`,
        after: (other) => ` below “${other}”`,
        before: (other) => ` above “${other}”`,
        into: (other) => ` into “${other}”`,
        required: () => ', required',
    },
};
const SETTING_WORDS = {
    fr: { label: 'libellé', description: 'description', required: 'obligatoire', widget: 'saisie', placeholder: 'texte indicatif', unit: 'unité', min: 'minimum', max: 'maximum', step: 'pas', default_value: 'valeur au départ', enum_values: 'choix', size: 'taille', region: 'zone', emphasis: 'mise en avant', columns: 'colonnes', collapsible: 'repli', page_label: 'nom de la page' },
    en: { label: 'label', description: 'description', required: 'required', widget: 'input', placeholder: 'placeholder', unit: 'unit', min: 'minimum', max: 'maximum', step: 'step', default_value: 'starting value', enum_values: 'choices', size: 'size', region: 'area', emphasis: 'emphasis', columns: 'columns', collapsible: 'collapse', page_label: 'page name' },
};
const phrases = (locale) => PHRASES[locale] ?? PHRASES['fr'];
const settingWord = (locale, setting) => (SETTING_WORDS[locale] ?? SETTING_WORDS['fr'])[setting] ?? setting;
// ---------------------------------------------------------------- Compilation
const refuse = (code, message, candidates) => ({ ok: false, refusal: { code, message, ...(candidates === undefined ? {} : { candidates }) } });
/** Pointeur d'insertion dans un tableau, d'après la position voulue. */
function insertionIndex(length, siblingIndex, side) {
    if (siblingIndex === undefined || side === undefined)
        return length;
    return side === 'after' ? siblingIndex + 1 : siblingIndex;
}
function sideOf(position) {
    if (position.after !== undefined)
        return { side: 'after', sibling: position.after };
    if (position.before !== undefined)
        return { side: 'before', sibling: position.before };
    return {};
}
function whereText(locale, body, position) {
    const p = phrases(locale);
    const { side, sibling } = sideOf(position);
    if (side !== undefined && sibling !== undefined) {
        const located = locateFormElement(body, sibling, locale);
        if (located !== null)
            return p[side](located.label);
    }
    const container = locateFormElement(body, position.container, locale);
    return container === null ? '' : p['into'](container.label);
}
/** Chemin d'un réglage dans un élément, ou `null` si la combinaison n'est pas prévue. */
function settingPath(kind, setting, located, ref) {
    if (kind === 'field') {
        const field = { label: '/field/label', description: '/field/description', required: '/field/required', unit: '/field/unit', default_value: '/field/default', min: '/field/validation/min', max: '/field/validation/max', enum_values: '/field/validation/enum_values', widget: '/presentation/widget', placeholder: '/presentation/placeholder', step: '/presentation/step', size: '/presentation/size' };
        return field[setting] === undefined ? null : `${located.pointer}${field[setting]}`;
    }
    if (kind === 'section') {
        if (setting === 'label')
            return `${located.pointer}/label`;
        if (['region', 'size', 'emphasis', 'columns', 'collapsible'].includes(setting))
            return `/layout/placements/${escape(String(ref.section_id))}/${setting}`;
        return null;
    }
    if (kind === 'page')
        return setting === 'page_label' || setting === 'label' ? `${located.pointer}/label` : null;
    return ['label', 'region', 'size', 'emphasis'].includes(setting) ? `${located.pointer}/${setting}` : null;
}
/** `add` ou `replace` selon que la feuille existe, en créant les objets intermédiaires absents. */
function setOps(body, pointer, value) {
    const ops = [];
    const parts = tokens(pointer);
    let node = body;
    let path = '';
    for (let i = 0; i < parts.length - 1; i += 1) {
        const part = parts[i];
        path += `/${escape(part)}`;
        const next = Array.isArray(node) ? node[Number(part)] : node !== null && typeof node === 'object' ? node[part] : undefined;
        if (next === undefined) {
            // Objet absent : on l'ajoute avec le reste du chemin en une fois.
            let nested = value;
            for (let j = parts.length - 1; j > i; j -= 1)
                nested = { [parts[j]]: nested };
            ops.push({ op: 'add', path, value: nested });
            return ops;
        }
        node = next;
    }
    const leaf = parts[parts.length - 1];
    const exists = node !== null && typeof node === 'object' && (Array.isArray(node) ? Number(leaf) < node.length : leaf in node);
    ops.push({ op: exists ? 'replace' : 'add', path: pointer, value });
    return ops;
}
function localizedValue(value, previous, locale) {
    if (typeof value !== 'string')
        return value;
    const base = previous !== null && typeof previous === 'object' ? { ...previous } : {};
    return { ...base, [locale]: value };
}
const LOCALIZED_SETTINGS = new Set(['label', 'description', 'placeholder', 'page_label']);
function readAt(body, pointer) {
    try {
        return read(body, pointer);
    }
    catch {
        return undefined;
    }
}
function compileAdd(body, raw, options) {
    const { element, position } = raw;
    const p = phrases(options.locale);
    if (element === undefined || position === undefined)
        return refuse('UNSUPPORTED', 'Il manque l’élément à ajouter ou sa place.');
    const label = text(element.label, options.locale, '');
    if (label === '')
        return refuse('UNSUPPORTED', 'L’élément à ajouter n’a pas de nom.');
    const { side, sibling } = sideOf(position);
    if (element.element_kind === 'field') {
        if (element.preset_ref !== undefined)
            return refuse('UNSUPPORTED', 'Un préréglage s’insère depuis la bibliothèque.');
        if (element.model_field !== undefined)
            return refuse('UNSUPPORTED', 'Un champ du modèle s’insère depuis la bibliothèque.');
        // Un champ de repère désigné sans sa partie : celle du conteneur, sinon la seule partie qui porte ce champ.
        const anchor = withSiblingSection(body, position.container, sibling);
        // La partie d'accueil : le conteneur, ou la partie du frère désigné.
        const sectionRef = position.container.element_kind === 'section' ? position.container : anchor?.element_kind === 'field' && anchor.section_id !== undefined ? { element_kind: 'section', section_id: anchor.section_id } : undefined;
        if (sectionRef === undefined)
            return refuse('AMBIGUOUS', 'Dans quelle partie placer ce champ ?');
        const section = locateFormElement(body, sectionRef, options.locale);
        if (section === null)
            return refuse('UNKNOWN_TARGET', 'Cette partie n’existe pas dans le formulaire.');
        const siblingAt = anchor === undefined ? undefined : locateFormElement(body, anchor, options.locale);
        if (anchor !== undefined && (siblingAt === null || siblingAt === undefined || siblingAt.section_index !== section.index))
            return refuse('UNKNOWN_TARGET', 'Le champ de repère n’est pas dans cette partie.');
        const fieldId = unique(element.field_id ?? formElementSlug(label), fieldIds(body));
        const type = element.field_type ?? (element.enum_values !== undefined ? 'enumeration' : 'string');
        const field = { field_id: fieldId, label: element.label, type, ...(element.required === undefined ? {} : { required: element.required }), ...(element.enum_values === undefined ? {} : { validation: { enum_values: element.enum_values } }) };
        const entry = { field, ...(element.widget === undefined ? {} : { presentation: { widget: element.widget } }) };
        const count = body.sections[section.index]['fields'].length;
        const at = insertionIndex(count, siblingAt?.index, side);
        const summary = p['add'](label, whereText(options.locale, body, position)) + (element.required === true ? p['required']() : '');
        return { ok: true, patch: [{ op: 'add', path: `${section.pointer}/fields/${at}`, value: entry }], summary, target: { element_kind: 'field', section_id: String(sectionRef.section_id), field_id: fieldId }, element: { ...element, field_id: fieldId } };
    }
    if (element.element_kind === 'section') {
        const taken = new Set(body.sections.map((section) => String(section['section_id'])));
        const sectionId = unique(element.section_id ?? formElementSlug(label), taken);
        const siblingAt = sibling?.element_kind === 'section' ? locateFormElement(body, sibling, options.locale) : undefined;
        if (siblingAt === null)
            return refuse('UNKNOWN_TARGET', 'La partie de repère n’existe pas.');
        const at = insertionIndex(body.sections.length, siblingAt?.index, side);
        const patch = [{ op: 'add', path: `/sections/${at}`, value: { section_id: sectionId, label: element.label, fields: [] } }];
        if (position.container.element_kind === 'page') {
            const page = locateFormElement(body, position.container, options.locale);
            if (page === null) {
                if (pagesOf(body) !== undefined || position.container.page_id !== 'main')
                    return refuse('UNKNOWN_TARGET', 'Cette page n’existe pas.');
            }
            else
                patch.push({ op: 'add', path: `${page.pointer}/sections/-`, value: sectionId });
        }
        return { ok: true, patch, summary: p['add'](label, whereText(options.locale, body, position)), target: { element_kind: 'section', section_id: sectionId }, element: { ...element, section_id: sectionId } };
    }
    // Bloc d'affichage : seulement un composant admis, dans une région nommée par le conteneur page.
    const component = element.block_component;
    if (component === undefined || !(options.blockComponents ?? new Set()).has(component))
        return refuse('NOT_ALLOWED_IN_FORM', 'Ce bloc ne peut pas être ajouté à un formulaire.');
    const blocks = blocksOf(body);
    const taken = new Set((blocks ?? []).map((block) => String(block['block_id'])));
    const blockId = unique(formElementSlug(label), taken);
    const page = position.container.element_kind === 'page' ? locateFormElement(body, position.container, options.locale) : null;
    const value = { block_id: blockId, component, region: 'header', label: element.label, ...(page === null ? {} : { page_ids: [position.container.page_id] }) };
    const patch = blocks === undefined ? setOps(body, '/layout/blocks', [value]) : [{ op: 'add', path: '/layout/blocks/-', value }];
    return { ok: true, patch, summary: p['add'](label, whereText(options.locale, body, position)), target: { element_kind: 'block', block_id: blockId } };
}
function compileMove(body, raw, options) {
    const { target, position } = raw;
    if (target === undefined || position === undefined)
        return refuse('UNSUPPORTED', 'Il manque l’élément à déplacer ou sa nouvelle place.');
    const located = locateFormElement(body, target, options.locale);
    if (located === null)
        return refuse('UNKNOWN_TARGET', 'Cet élément n’existe pas dans le formulaire.');
    const { side, sibling } = sideOf(position);
    const summary = phrases(options.locale)['move'](located.label, whereText(options.locale, body, position));
    if (target.element_kind === 'field') {
        const sectionRef = position.container.element_kind === 'section' ? position.container : sibling?.element_kind === 'field' ? { element_kind: 'section', section_id: sibling.section_id } : undefined;
        if (sectionRef === undefined)
            return refuse('AMBIGUOUS', 'Dans quelle partie déplacer ce champ ?');
        const section = locateFormElement(body, sectionRef, options.locale);
        if (section === null)
            return refuse('UNKNOWN_TARGET', 'Cette partie n’existe pas dans le formulaire.');
        const siblingAt = sibling === undefined ? undefined : locateFormElement(body, sibling, options.locale);
        if (siblingAt === null)
            return refuse('UNKNOWN_TARGET', 'Le champ de repère n’existe pas.');
        // Indice calculé après le retrait de l'élément (sémantique de `move`).
        const fields = body.sections[section.index]['fields'];
        const sameSection = located.section_index === section.index;
        let siblingIndex = siblingAt?.index;
        if (sameSection && siblingIndex !== undefined && siblingIndex > located.index)
            siblingIndex -= 1;
        if (sameSection && siblingAt !== undefined && siblingAt !== null && siblingAt.index === located.index)
            return refuse('UNSUPPORTED', 'Un élément ne se place pas par rapport à lui-même.');
        const at = insertionIndex(fields.length - (sameSection ? 1 : 0), siblingIndex, side);
        return { ok: true, patch: [{ op: 'move', from: located.pointer, path: `${section.pointer}/fields/${at}` }], summary, target };
    }
    if (target.element_kind === 'section' || target.element_kind === 'page' || target.element_kind === 'block') {
        if (sibling === undefined)
            return refuse('AMBIGUOUS', 'Avant ou après quel élément ?');
        if (sibling.element_kind !== target.element_kind)
            return refuse('UNSUPPORTED', 'Un élément se déplace parmi ceux de sa sorte.');
        const siblingAt = locateFormElement(body, sibling, options.locale);
        if (siblingAt === null)
            return refuse('UNKNOWN_TARGET', 'L’élément de repère n’existe pas.');
        if (siblingAt.index === located.index)
            return refuse('UNSUPPORTED', 'Un élément ne se place pas par rapport à lui-même.');
        const base = located.pointer.slice(0, located.pointer.lastIndexOf('/'));
        const siblingIndex = siblingAt.index > located.index ? siblingAt.index - 1 : siblingAt.index;
        const at = insertionIndex(Number.NaN, siblingIndex, side);
        return { ok: true, patch: [{ op: 'move', from: located.pointer, path: `${base}/${at}` }], summary, target };
    }
    return refuse('UNSUPPORTED', 'Cet élément ne se déplace pas.');
}
function compileUpdate(body, raw, options) {
    const { target, changes } = raw;
    if (target === undefined || changes === undefined || changes.length === 0)
        return refuse('UNSUPPORTED', 'Il manque l’élément ou le réglage à modifier.');
    const located = locateFormElement(body, target, options.locale);
    if (located === null)
        return refuse('UNKNOWN_TARGET', 'Cet élément n’existe pas dans le formulaire.');
    const patch = [];
    const written = [];
    let working = body;
    for (const change of changes) {
        const pointer = settingPath(target.element_kind, change.setting, located, target);
        if (pointer === null)
            return refuse('UNSUPPORTED', `« ${settingWord(options.locale, change.setting)} » ne se règle pas sur cet élément.`);
        const previous = readAt(working, pointer);
        const value = LOCALIZED_SETTINGS.has(change.setting) ? localizedValue(change.value, previous, options.locale) : change.value;
        const ops = setOps(working, pointer, value);
        working = applyFormPatch(working, ops);
        patch.push(...ops);
        written.push({ setting: change.setting, value: change.value, ...(previous === undefined ? {} : { previous }) });
    }
    const how = written.map((change) => settingWord(options.locale, change.setting)).join(', ');
    return { ok: true, patch, summary: phrases(options.locale)['update'](located.label, how), target, changes: written };
}
function compileRemove(body, raw, options) {
    const { target } = raw;
    if (target === undefined)
        return refuse('UNSUPPORTED', 'Il manque l’élément à supprimer.');
    const located = locateFormElement(body, target, options.locale);
    if (located === null)
        return refuse('UNKNOWN_TARGET', 'Cet élément n’existe pas dans le formulaire.');
    const patch = [];
    const summary = phrases(options.locale)['remove'](located.label);
    if (target.element_kind === 'section') {
        // Retirée aussi des pages qui la citent et de son placement, des derniers indices aux premiers.
        const pages = pagesOf(body) ?? [];
        pages.forEach((page, pageIndex) => {
            const sections = page['sections'];
            const at = sections.indexOf(String(target.section_id));
            if (at >= 0)
                patch.push({ op: 'remove', path: `/layout/pages/${pageIndex}/sections/${at}` });
        });
        const placements = body['layout']?.['placements'];
        if (placements !== undefined && String(target.section_id) in placements)
            patch.push({ op: 'remove', path: `/layout/placements/${escape(String(target.section_id))}` });
        patch.push({ op: 'remove', path: located.pointer });
        return { ok: true, patch, summary, target };
    }
    if (target.element_kind === 'page' && raw.remove_fields === true) {
        const page = (pagesOf(body) ?? [])[located.index];
        const cited = new Set(page['sections']);
        patch.push({ op: 'remove', path: located.pointer });
        const indices = body.sections.map((section, i) => (cited.has(String(section['section_id'])) ? i : -1)).filter((i) => i >= 0).reverse();
        for (const i of indices)
            patch.push({ op: 'remove', path: `/sections/${i}` });
        return { ok: true, patch, summary, target };
    }
    patch.push({ op: 'remove', path: located.pointer });
    return { ok: true, patch, summary, target };
}
/**
 * Compile une opération du modèle en patch sur le corps donné, avec sa phrase. Un refus dit pourquoi
 * en une phrase ; il n'y a jamais de réparation silencieuse. Le patch est vérifié applicable ici ; la
 * vérification du formulaire obtenu (gate de l'éditeur) revient à l'appelant.
 */
export function compileFormEdit(body, raw, options) {
    let compiled;
    if (raw.kind === 'add_element')
        compiled = compileAdd(body, raw, options);
    else if (raw.kind === 'move_element')
        compiled = compileMove(body, raw, options);
    else if (raw.kind === 'update_element')
        compiled = compileUpdate(body, raw, options);
    else if (raw.kind === 'remove_element')
        compiled = compileRemove(body, raw, options);
    else
        return refuse('UNSUPPORTED', 'Cette opération n’existe pas.');
    if (!compiled.ok)
        return compiled;
    try {
        applyFormPatch(body, compiled.patch);
    }
    catch {
        return refuse('UNSUPPORTED', 'Cette modification ne s’applique pas à ce formulaire.');
    }
    return compiled;
}
//# sourceMappingURL=form-edit-operations.js.map